<script lang="ts" setup>
import type { UploadFile } from 'element-plus'
import { ref } from 'vue'
import { useProductStore } from '@/stores/product'
import { UploadOutlinedIcon } from '@xpos/icons-svg'
import ImageCropperDialog from '@/components/ImageCropperDialog.vue'
import ImageDeleteButton from '@/components/image/ImageDeleteButton.vue'
import { fileToDataUrl } from '@/utils/misc'
import { compressImage } from '@/composables/useImageUpload'

const $props = withDefaults(defineProps<{
  modelValue?: string
  rootFormData?: any
  enableCrop?: boolean
  aspectRatio?: number
  maxSizeMB?: number
  maxWidthOrHeight?: number
}>(), {
  enableCrop: false,
  maxSizeMB: 0.5,
  maxWidthOrHeight: 1280,
})

const $emit = defineEmits(['change', 'update:value', 'update:modelValue'])
const productStore = useProductStore()
const showCropper = ref(false)
const cropImageSrc = ref('')
const cropperRef = ref<InstanceType<typeof ImageCropperDialog>>()

// Uploads processed blob via product service
const uploadBlob = async (blob: Blob) => {
  const productService = await productStore.getItemService()
  const file = new File([blob], 'image.jpg', { type: blob.type || 'image/jpeg' })
  await productService.addImageAsync(file, $props.rootFormData)
}

const handleUpload = async (file: UploadFile) => {
  if (!file.raw) return
  if ($props.enableCrop) {
    cropImageSrc.value = await fileToDataUrl(file.raw)
    showCropper.value = true
    return
  }
  await uploadBlob(await compressImage(file.raw, { maxSizeMB: $props.maxSizeMB, maxWidthOrHeight: $props.maxWidthOrHeight }))
}

const handleCropConfirm = async (blob: Blob) => {
  try {
    await uploadBlob(await compressImage(blob, { maxSizeMB: $props.maxSizeMB, maxWidthOrHeight: $props.maxWidthOrHeight }))
    cropperRef.value?.close()
  } catch {
    cropperRef.value?.onError()
  }
}
</script>

<template>
  <el-space :size="0">
    <div class="relative" v-if="$props.modelValue">
      <img class="w-[45px] h-[45px]" :src="$props.modelValue" />
      <!-- Clear both fields set by addImageAsync; menu and lists render image_thumbnail_url -->
      <ImageDeleteButton @click="Object.assign($props.rootFormData, { image_url: '', image_thumbnail_url: '' })" />
    </div>
    <el-upload class="avatar-uploader ml-[5px] mr-0" :show-file-list="false" :on-change="handleUpload" :auto-upload="false" accept="image/*">
      <el-button type="primary" class="shrink-0 w-[50px]"
        ><el-icon size="20"><UploadOutlinedIcon /></el-icon
      ></el-button>
    </el-upload>
    <ImageCropperDialog ref="cropperRef" v-model="showCropper" :image-src="cropImageSrc"
      :aspect-ratio="$props.aspectRatio" @confirm="handleCropConfirm" />
  </el-space>
</template>
